import { useEffect } from "react";

import { useLocation, useParams, Switch, Route, useRouteMatch, Redirect } from "react-router-dom";

import { useOrganisationContext } from "@/contexts/organisations/OrganisationContext";
import { useDidUpdate } from "@/hooks/useDidUpdate";
import useUserProfile from "@/hooks/useUserProfile";
import { ViewerContext } from "@/tpo/Viewer";
import { gql, useQuery } from "@apollo/client";

import {
  CMP_PATIENTS_PROFILE_URL,
  CMP_PRACTITIONER_URL,
  ERROR_404_URL,
  getCmpUrl
} from "../../core/urls";
import Loading from "../Loading";
import ProfileTab from "./profile/Profile";
import PatientQuestionnaire from "./questionnaire/Questionnaire";
import PatientReportDetail from "./reports/ReportDetail";
import PatientReportPrint from "./reports/ReportPrint";
import PatientReports from "./reports/Reports";
import Test from "./testResults/TestTab";
import TestsRoute from "./testResults/TestsRoute";

function PractitionerReportsGuard({ children }) {
  const { patientId } = useParams();
  const { organisation } = useOrganisationContext();
  const { practitionerProfile, loading } = useUserProfile();

  if (!practitionerProfile) {
    return children;
  }

  if (loading) {
    return <Loading />;
  }

  if (!practitionerProfile.showReportsTab) {
    return (
      <Redirect
        to={getCmpUrl(CMP_PATIENTS_PROFILE_URL, {
          organisationId: organisation?.hashId,
          patientId
        })}
      />
    );
  }

  return children;
}

function PatientView() {
  const match = useRouteMatch();
  const { patientId } = useParams();
  const { organisation } = useOrganisationContext();

  const { practitionerProfile, loading } = useUserProfile();
  const isPractitioner = !!practitionerProfile;
  const hideReportsTab =
    isPractitioner && (loading || practitionerProfile?.showReportsTab === false);

  const templateProps = {
    jumbotronProps: {
      bg: "partners",
      color: "dark"
    },
    excludeTabs: []
  };

  if (!isPractitioner && !loading) {
    templateProps.jumbotronProps.bg = "purple";
    templateProps.jumbotronProps.color = "white";
    templateProps.excludeTabs = ["profile", "dashboard", "self_assessment"];
  }

  if (hideReportsTab) {
    templateProps.excludeTabs = [...templateProps.excludeTabs, "reports"];
  }

  const profileUrl = getCmpUrl(CMP_PATIENTS_PROFILE_URL, {
    organisationId: organisation?.hashId,
    patientId
  });

  return (
    <ViewerContext.Provider
      value={{
        userId: patientId,
        isPractitioner
      }}
    >
      <Switch>
        <Route path={match.path} exact>
          <Redirect to={profileUrl} />
        </Route>
        <Route path={`${match.path}/profile`} exact>
          <ProfileTab templateProps={templateProps} />
        </Route>
        <Redirect from={`${match.path}/tests`} to={`${match.path}/test-results`} />
        <Redirect
          from={`${match.path}/tests/:userTestId`}
          to={`${match.path}/test-results/:userTestId`}
        />
        <Route path={`${match.path}/test-results`} exact>
          <TestsRoute templateProps={templateProps} />
        </Route>
        <Route path={`${match.path}/test-results/:userTestId`} exact>
          <Test templateProps={templateProps} />
        </Route>
        <Route path={`${match.path}/reports`} exact>
          <PractitionerReportsGuard>
            <PatientReports templateProps={templateProps} />
          </PractitionerReportsGuard>
        </Route>
        <Route path={`${match.path}/reports/:reportSlug/print`} exact>
          <PatientReportPrint />
        </Route>
        <Redirect
          from={`${match.path}/reports/:reportSlug`}
          to={`${match.path}/reports/:reportSlug/overview`}
          exact
        />
        <Route path={`${match.path}/reports/:reportSlug/:tab`} exact>
          <PractitionerReportsGuard>
            <PatientReportDetail templateProps={templateProps} />
          </PractitionerReportsGuard>
        </Route>
        <Route path={`${match.path}/questionnaire`} exact>
          <PatientQuestionnaire templateProps={templateProps} />
        </Route>
        <Redirect to={ERROR_404_URL} />
      </Switch>
    </ViewerContext.Provider>
  );
}

const IS_CONNECTED_TO_PATIENT = gql`
  query IsConnectedToPatient($patientId: ID!, $organisationId: ID!) {
    isConnectedToPatient(patientId: $patientId, organisationId: $organisationId)
  }
`;

export default function PatientViewRoutes() {
  const match = useRouteMatch();
  const location = useLocation();
  const { patientId } = useParams();
  const { organisationId, organisationNotFound } = useOrganisationContext();

  const { loading, practitionerProfile } = useUserProfile();

  const { data, refetch } = useQuery(IS_CONNECTED_TO_PATIENT, {
    variables: {
      patientId,
      organisationId
    },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first",
    skip: !organisationId
  });

  useDidUpdate(() => {
    refetch();
  }, [location.pathname, refetch]);

  useEffect(() => {
    if (!loading && data && !data?.isConnectedToPatient && !practitionerProfile) {
      window.location.href = "https://www.regeneruslabs.me";
    }
  }, [loading, practitionerProfile, data]);

  if (!loading && practitionerProfile && !practitionerProfile.approved) {
    return <Redirect to={CMP_PRACTITIONER_URL} />;
  }

  if (!data && (loading || (!loading && !practitionerProfile))) return <Loading />;

  if (!loading && data && !data?.isConnectedToPatient && practitionerProfile) {
    return <Redirect to={ERROR_404_URL} />;
  }

  if (organisationNotFound) {
    return <Redirect to={ERROR_404_URL} />;
  }

  return (
    <Switch>
      <Route path={`${match.path}`} component={PatientView} />
    </Switch>
  );
}
